import type { IndexFieldType } from './field-type';

export const IndexerSchema = {
  doc: {
    docId: 'String',
    title: 'FullText',
    // summary of the doc, used for preview
    summary: { type: 'String', index: false },
    // file names of the page's attachments, from the server search catalog
    fileNames: 'FullText',
  },
  block: {
    docId: 'String',
    blockId: 'String',
    content: 'FullText',
    flavour: 'String',
    // normalized source category for result filters (text/image/database/...)
    sourceType: 'String',
    blob: 'String',
    // reference doc id
    // ['xxx','yyy']
    refDocId: 'String',
    // reference info, used for backlink to specific block
    // [{"docId":"xxx","mode":"page","blockIds":["gt5Yfq1maYvgNgpi13rIq"]},{"docId":"yyy","mode":"edgeless","blockIds":["k5prpOlDF-9CzfatmO0W7"]}]
    ref: { type: 'String', index: false },
    // parent block flavour
    parentFlavour: 'String',
    // parent block id
    parentBlockId: 'String',
    // additional info
    // { "databaseName": "xxx", "displayMode": "page/edgeless", "noteBlockId": "xxx" }
    additional: { type: 'String', index: false },
    markdownPreview: { type: 'String', index: false },
  },
  mail: {
    // the mailbox page; the right to read it is the right to read the mail
    docId: 'String',
    messageId: 'String',
    // mail account and folder, to open the mail
    accountId: { type: 'String', index: false },
    folderId: { type: 'String', index: false },
    subject: 'FullText',
    // names and addresses of sender and recipients
    participants: 'FullText',
    content: 'FullText',
    snippet: { type: 'String', index: false },
    // ISO 8601 time the mail arrived
    receivedAt: { type: 'String', index: false },
    // hash of the indexed mail text, compared with the server's
    contentHash: { type: 'String', index: false },
  },
} satisfies Record<string, Record<string, IndexerFieldSchema>>;

/**
 * Flavour of the block-table documents that hold the row text of one row
 * shard of a database. They are no blocks and have no `blockId`;
 * `parentBlockId` is the database block; `additional` carries
 * `databaseRootDocId`, `shardDocId` and `shardVersion` (base64 state vector
 * of the shard as indexed). It shares no word with a block flavour, because
 * the SQLite index matches flavours word by word.
 */
export const DATABASE_ROW_SHARD_FLAVOUR = 'streamlain:rowshard';

export type IndexerFieldSchema =
  | IndexFieldType
  | {
      type: IndexFieldType;
      /**
       * If false, the field will not be indexed, and thus not searchable.
       *
       * default: true
       */
      index?: boolean;
      /**
       * If false, the field will not be stored, and not included in the search result.
       *
       * default: true
       */
      store?: boolean;
    };

export type IndexerSchema = typeof IndexerSchema;

/** Every table of the local search index. */
export const INDEXER_TABLES = Object.keys(
  IndexerSchema
) as (keyof IndexerSchema)[];

export function isIndexerTable(table: string): table is keyof IndexerSchema {
  return Object.prototype.hasOwnProperty.call(IndexerSchema, table);
}
